import {
  AiReviewStatus,
  DispatchStatus,
  JobState,
  OrderState,
  SiteType,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { isNil, pick, uniqBy } from 'lodash-es';
import {
  action,
  computed,
  IComputedValue,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';

import {
  DEFAULT_TAB,
  orderStatesByStatusTab,
  StatusTabValue,
} from '~components/Filters/OrdersStatusFilter';
import { OrderOption } from '~components/OrderBySelector/OrderBySelector';
import { ORDERS_TAB_ORDER_STATES, OrdersDispatchStorageKeys } from '~constants/dispatch';
import { AccountCapacityTypeahead } from '~hooks/useAccount/models';
import { DriverTypeahead } from '~hooks/useDrivers/models';
import { Job } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import { Project } from '~hooks/useProjects/models';
import { Pagination } from '~services/pagination';
import { rootStore } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { RTU_WAITING_TIMEOUT_IN_MS } from '~utils/rtu/rtu-utils';

const defaultPaginationState: Pagination = { limit: 20, page: 1 };

const TODAY_DATE_STORE_KEY = 'today';

export enum ExpandedState {
  CLOSED = 'closed',
  OPEN = 'open',
}
const ORDER_DISPATCH_CARD_DEFAULT_EXPANDED_STATE = ExpandedState.OPEN;

type OrderCardsExpandedState = {
  allCards: string;
  cardsByOrder: Map<string, string>;
};

export type OrderCardsExpandedStateStorageKey =
  | OrdersDispatchStorageKeys.EXPANDED_STATE
  | OrdersDispatchStorageKeys.GRID_EXPANDED_STATE;

export const isOrderExpanded = (
  { allCards, cardsByOrder }: OrderCardsExpandedState,
  orderId: string,
) => (cardsByOrder.get(orderId) ?? allCards) === ExpandedState.OPEN;

type OrdersCount = {
  ordersExternal: number;
  ordersExternalOnly: number;
  ordersPendingRequest: number;
  ordersTotal: number;
};

type DeepLinkedJob = {
  id: string;
  jobStartAt: Nullable<Dayjs>;
};

type DeepLinkOptions = {
  job?: DeepLinkedJob;
  source: 'created' | 'existing';
};

export type PendingDeepLink = {
  job?: DeepLinkedJob;
  order: Order;
};

export type Filters = {
  aiReviewStatuses?: AiReviewStatus[];
  collaboratorIds?: string[];
  customerAccountIds?: string[];
  departmentIds?: string[];
  dispatchNumberIds?: string[];
  dispatchStatuses?: DispatchStatus[];
  driverIds?: string[];
  dropoffSites?: string[];
  endDate?: string;
  equipmentIds?: string[];
  // Resolved owner company ids of the assigned truck (own company for internal trucks)
  equipmentOwnerCompanyIds?: string[];
  equipmentTypes?: string[];
  // Drives the dispatch External tab: when true the index sends filter[external]=true (the
  // received-orders union) instead of filter[states]. Set from the selected status tab.
  external?: boolean;
  foremen?: string[];
  hasUnreadMessages?: boolean;
  jobStates?: JobState[];
  materialIds?: string[];
  orderStates?: OrderState[];
  pickUpSites?: string[];
  prevailingWage?: string[];
  projectIds?: string[];
  projectsExternalIds?: string[];
  refinedOrderStates?: OrderState[];
  requesterIds?: string[];
  salespersonIds?: string[];
  search?: string;
  serviceClassIds?: string[];
  siteTypes?: SiteType[];
  startDate?: string;
  ticketPotentialDuplicate?: string[];
  ticketTypes?: string[];
  vendorAccountIds?: string[];
};

// The day the dispatch status counts are for: the dispatch date, or today when none is picked.
export function getDispatchStatusDate(filters: Filters) {
  return filters.startDate ?? dayjs.tz().format('YYYY-MM-DD');
}

export enum OrdersSort {
  ACCOUNT_NAME = 'account_name',
  COMPANY_LEGAL_NAME = 'company_legal_name',
  CREATED_AT = 'created_at',
  DISPATCH_NUMBER_NAME = 'dispatch_number',
  FRIENDLY_ORDER_ID = 'friendly_order_id',
  LOAD_AT = 'load_at',
  TRUCK_COUNT = 'truck_count',
}

const defaultOrdersSort: OrderOption<OrdersSort> = {
  orderBy: OrdersSort.DISPATCH_NUMBER_NAME,
  sortBy: 'desc',
};

const areOrderSortOptionsEqual = (
  left: OrderOption<OrdersSort>,
  right: OrderOption<OrdersSort>,
) => {
  return left.orderBy === right.orderBy && left.sortBy === right.sortBy;
};

class OrdersDispatchStore {
  acceptingOrRejectingOrderId: string = '';
  // When a search returns nothing inside the selected date range but there ARE matches on
  // other dates, dispatch auto-drops the dates and remembers the prior range here (keyed by the
  // committed search term) so the DateScopeBanner can offer to restore it (TRE-15947). This is a
  // pure record — banner VISIBILITY is DERIVED in OrdersView from the term matching the current
  // search plus all-dates being selected (TRE-15949), so setFilters never has to clear it.
  autoExtendedFromRange: { term: string; startDate?: string; endDate?: string } | null =
    null;
  cancellingJobId: string = '';
  deepLinkedJobId?: string;
  deepLinkedOrder?: Order;
  deletingJobId?: string;
  draggedAssignee?: DriverTypeahead | AccountCapacityTypeahead;
  draggedAssigneeEquipmentTypeId?: string;
  driverPanelAssignees: Array<DriverTypeahead | AccountCapacityTypeahead> = [];
  duplicatingJobId: string = '';
  filters: Filters = { search: '' };
  hasMore: boolean = false;
  // True when a search returns results inside the selected date range AND there are also
  // matches on other dates — drives the DateScopeBanner's 'scoped-to-range' variant offering
  // to show all dates. Recomputed every fetch (reset in resetOrders), unlike autoExtendedFromRange
  // (TRE-15949).
  hasResultsOutsideDateRange: boolean = false;
  hasUnfilteredResults: boolean = false;
  isAcceptingOrRejectingJobs: Map<string, boolean> = new Map();
  isCancellingOrder: boolean = false;
  isCheckingOtherDateResults: boolean = false;
  isCloningOrder: boolean = false;
  isCompletingOrder: boolean = false;
  isCreatingJobFromOrder: boolean = false;
  isDeepLinkedOrderNewlyCreated: boolean = false;
  hasOrderJobsFetchFailed: Map<string, boolean> = new Map();
  isLoadingOrderJobs: Map<string, boolean> = new Map();
  isLoadingOrderProject: Map<string, boolean> = new Map();
  isLoadingOrders: boolean = false;
  isSavingOrder: boolean = false;
  isSendingJobs: Map<string, boolean> = new Map();
  isSidebarCollapsed?: boolean;
  isUpdatingOrderJob: Map<string, boolean> = new Map();
  jobsWaitingForRTU: Array<{ jobId: string; friendlyJobId: string }> = [];
  orderCardsDefaultExpandedState: ExpandedState =
    ORDER_DISPATCH_CARD_DEFAULT_EXPANDED_STATE;
  orderCardsExpandedState: OrderCardsExpandedState = {
    allCards: ORDER_DISPATCH_CARD_DEFAULT_EXPANDED_STATE,
    cardsByOrder: new Map(),
  };
  orderCardsExpandedStateStorageKey: OrderCardsExpandedStateStorageKey =
    OrdersDispatchStorageKeys.EXPANDED_STATE;
  defaultOrdersSortOption: OrderOption<OrdersSort> = { ...defaultOrdersSort };
  lastEvaluatedDefaultOrdersSort: OrderOption<OrdersSort> | null = null;
  orderJobs: Map<string, Job[]> = new Map();
  private readonly orderJobsSnapshotsByOrderId = new Map<string, IComputedValue<Job[]>>();
  orderProject: Map<string, Project | null> = new Map();
  orders: Order[] = [];
  ordersCount?: OrdersCount;
  ordersSort: OrderOption<OrdersSort> = { ...defaultOrdersSort };
  ordersWaitingForTrucksCountToMatchCountInSummary: Map<string, Dayjs> = new Map();
  pagination: Pagination = { ...defaultPaginationState };
  pendingDeepLink?: PendingDeepLink;
  preventUpsertOrder: boolean = false;
  pendingFiltersUpdateAfterAssigneeUpdate?: { label: string; value: string };
  pendingFiltersUpdateAfterAssigneeUpdateHasBeenCommitedInLocalStorageAvailebleOptions?: boolean;
  userChosenDateScopeTerm: string | null = null;

  constructor() {
    makeObservable(this, {
      acceptingOrRejectingOrderId: observable,
      autoExtendedFromRange: observable,
      cancellingJobId: observable,
      deepLinkedJobId: observable,
      deepLinkedOrder: observable,
      deletingJobId: observable,
      draggedAssignee: observable,
      draggedAssigneeEquipmentTypeId: observable,
      draggedAssigneeTruckCapacity: computed,
      driverPanelAssignees: observable,
      duplicatingJobId: observable,
      filters: observable,
      hasMore: observable,
      hasResultsOutsideDateRange: observable,
      hasUnfilteredResults: observable,
      isAcceptingOrRejectingJobs: observable,
      isCancellingOrder: observable,
      isCheckingOtherDateResults: observable,
      isCloningOrder: observable,
      isCompletingOrder: observable,
      isCreatingJobFromOrder: observable,
      isDeepLinkedOrderNewlyCreated: observable,
      isDateScopeAutoExtended: computed,
      hasOrderJobsFetchFailed: observable,
      isLoadingOrderJobs: observable,
      isLoadingOrderProject: observable,
      isLoadingOrders: observable,
      isSavingOrder: observable,
      isSendingJobs: observable,
      isSidebarCollapsed: observable,
      isUpdatingOrderJob: observable,
      jobsWaitingForRTU: observable,
      orderCardsDefaultExpandedState: observable,
      orderCardsExpandedState: observable,
      orderCardsExpandedStateStorageKey: observable,
      defaultOrdersSortOption: observable,
      orderJobs: observable,
      orderProject: observable,
      orders: observable,
      ordersCount: observable,
      orderJobsSnapshot: computed,
      ordersSnapshot: computed,
      ordersSort: observable,
      ordersWaitingForTrucksCountToMatchCountInSummary: observable,
      pagination: observable,
      pendingDeepLink: observable,
      pendingFiltersUpdateAfterAssigneeUpdate: observable,
      pendingFiltersUpdateAfterAssigneeUpdateHasBeenCommitedInLocalStorageAvailebleOptions:
        observable,
      preventUpsertOrder: observable,
      userChosenDateScopeTerm: observable,
      toggleOrderCardExpandedState: action.bound,
    });

    reaction(
      () => this.orders,
      (orders) => {
        orders.forEach((order) => {
          this.updateOrdersWaitingForTrucksCountToMatchCountInSummary(order);
        });
      },
    );
  }

  get hasActiveNonSearchFilters() {
    const {
      search: _search,
      orderStates: _orderStates,
      refinedOrderStates: _refinedOrderStates,
      ...rest
    } = this.filters;

    return Object.values(rest).some(
      (value) =>
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0),
    );
  }

  get isDateScopeAutoExtended() {
    const extendedTerm = this.autoExtendedFromRange?.term;

    return (
      Boolean(extendedTerm) &&
      extendedTerm === this.filters.search?.trim() &&
      extendedTerm !== this.userChosenDateScopeTerm &&
      !this.filters.startDate &&
      !this.filters.endDate
    );
  }

  get isFetchingOrdersForASingleDay() {
    return Boolean(
      this.filters.startDate && this.filters.startDate === this.filters.endDate,
    );
  }

  // A new array whenever `orders` changes, including in-place splices, for consumers that
  // memoize on identity.
  get ordersSnapshot() {
    return this.orders.slice();
  }

  // Same, for every order's jobs.
  get orderJobsSnapshot(): ReadonlyMap<string, Job[]> {
    return new Map(
      Array.from(this.orderJobs, ([orderId, jobs]) => [orderId, jobs.slice()]),
    );
  }

  // Same, for one order's jobs, which keeps its identity while other orders' jobs change.
  orderJobsSnapshotFor(orderId: string) {
    let snapshot = this.orderJobsSnapshotsByOrderId.get(orderId);

    if (!snapshot) {
      snapshot = computed(() => (this.orderJobs.get(orderId) ?? []).slice());
      this.orderJobsSnapshotsByOrderId.set(orderId, snapshot);
    }

    return snapshot.get();
  }

  // The search-aware empty states (search-term-has-no-results / results-exist-on-other-dates)
  // are only truthful once BOTH the orders fetch AND the out-of-range probe have settled. While
  // either is in flight — or during the brief gap after auto-extend clears the dates and before
  // the widened refetch starts — the counts read as an empty result set, so consumers MUST hold
  // off on the definitive empty states and keep showing the loading UI (TRE-15947).
  get isResolvingSearchScope() {
    return this.isLoadingOrders || this.isCheckingOtherDateResults;
  }

  updateOrdersWaitingForTrucksCountToMatchCountInSummary(order: Order) {
    const timestamp = this.ordersWaitingForTrucksCountToMatchCountInSummary.get(order.id);

    if (timestamp) {
      const trucksCountDidMatch = order.truckCount === order.orderSummary?.jobsCount;

      const millisecondsWaited = dayjs.tz().diff(timestamp);
      const trucksCountTimedOut = millisecondsWaited > RTU_WAITING_TIMEOUT_IN_MS;

      const shouldStopWaiting = trucksCountDidMatch || trucksCountTimedOut;

      if (shouldStopWaiting) {
        this.ordersWaitingForTrucksCountToMatchCountInSummary.delete(order.id);
      }
    }
  }

  jobCreateFromOrderStart(orderId: string) {
    runInAction(() => {
      this.isLoadingOrderJobs.set(orderId, true);
    });
  }

  jobCreateFromOrderEnd(orderId: string, job?: Job) {
    runInAction(() => {
      this.isLoadingOrderJobs.set(orderId, false);

      if (job) {
        this.upsertJob(job);
      }
    });
  }

  orderAcceptStart(orderId: string) {
    runInAction(() => {
      this.acceptingOrRejectingOrderId = orderId;
    });
  }

  orderAcceptEnd(order?: Order) {
    runInAction(() => {
      this.acceptingOrRejectingOrderId = '';

      if (order) {
        this.upsertOrder(order);
      }
    });
  }

  ordersFetchStart() {
    runInAction(() => {
      this.isLoadingOrders = true;
    });
  }

  ordersFetchEnd(
    orders: Order[],
    pagination?: Pagination,
    options?: { replaceExisting?: boolean },
  ) {
    runInAction(() => {
      this.isLoadingOrders = false;

      const shouldReplaceExisting =
        options?.replaceExisting ||
        !pagination ||
        (!pagination.after && !pagination.before);

      if (shouldReplaceExisting) {
        this.orders = orders;
      } else {
        this.orders = uniqBy(this.orders.concat(orders), (order) => order.id);
      }
      this.pagination = {
        limit: defaultPaginationState.limit,
        after: pagination?.after ?? '',
        page: defaultPaginationState.page,
      };
      this.hasMore = Boolean(pagination?.after);
    });
  }

  orderCancelStart() {
    runInAction(() => {
      this.isCancellingOrder = true;
    });
  }

  orderCancelEnd(order?: Order) {
    runInAction(() => {
      this.isCancellingOrder = false;

      if (order) {
        this.upsertOrder(order);
      }
    });
  }

  orderCloneStart() {
    runInAction(() => {
      this.isCloningOrder = true;
    });
  }

  orderCloneEnd(order?: Order) {
    runInAction(() => {
      this.isCloningOrder = false;

      if (order) {
        this.upsertOrder(order);
      }
    });
  }

  orderCompleteStart() {
    runInAction(() => {
      this.isCompletingOrder = true;
    });
  }

  orderCompleteEnd(order?: Order) {
    runInAction(() => {
      this.isCompletingOrder = false;

      if (order) {
        this.upsertOrder(order);
      }
    });
  }

  orderCreateStart() {
    runInAction(() => {
      this.isSavingOrder = true;
    });
  }

  orderCreateEnd(order?: Order) {
    runInAction(() => {
      this.isSavingOrder = false;

      if (order) {
        if (order.truckCount !== order.orderSummary?.jobsCount) {
          this.ordersWaitingForTrucksCountToMatchCountInSummary.set(order.id, dayjs.tz());

          setTimeout(() => {
            this.ordersWaitingForTrucksCountToMatchCountInSummary.delete(order.id);
          }, RTU_WAITING_TIMEOUT_IN_MS);
        }

        this.upsertOrder(order);
      }
    });
  }

  orderProjectFetchStart(orderId: string) {
    runInAction(() => {
      this.isLoadingOrderProject.set(orderId, true);
      this.orderProject.set(orderId, null);
    });
  }

  orderProjectFetchEnd(orderId: string, project?: Project) {
    runInAction(() => {
      this.isLoadingOrderProject.set(orderId, false);
      this.orderProject.set(orderId, project ?? null);
    });
  }

  orderRejectStart(orderId: string) {
    runInAction(() => {
      this.acceptingOrRejectingOrderId = orderId;
    });
  }

  orderRejectEnd(order?: Order) {
    runInAction(() => {
      this.acceptingOrRejectingOrderId = '';

      if (order) {
        this.upsertOrder(order);
      }
    });
  }

  orderUpdateStart() {
    runInAction(() => {
      this.isSavingOrder = true;
    });
  }

  orderUpdateEnd(order?: Order) {
    runInAction(() => {
      this.isSavingOrder = false;

      if (order) {
        this.upsertOrder(order);
      }
    });
  }

  orderJobsFetchStart(orderId: string, clearExistingJobs: boolean = true) {
    runInAction(() => {
      this.hasOrderJobsFetchFailed.set(orderId, false);
      this.isLoadingOrderJobs.set(orderId, true);
      if (clearExistingJobs) {
        this.orderJobs.set(orderId, []);
      }
    });

    // The map wraps the array, so the stored value is the identity to compare on abort.
    return clearExistingJobs ? this.orderJobs.get(orderId) : undefined;
  }

  orderJobsFetchAborted(orderId: string, emptyJobs: Job[] | undefined) {
    runInAction(() => {
      if (this.orderJobs.get(orderId) !== emptyJobs) {
        return;
      }

      this.isLoadingOrderJobs.delete(orderId);
      this.orderJobs.delete(orderId);
    });
  }

  orderJobsFetchFailed(orderId: string) {
    runInAction(() => {
      this.hasOrderJobsFetchFailed.set(orderId, true);
      this.isLoadingOrderJobs.set(orderId, false);

      if (this.deepLinkedOrder?.id === orderId && !this.isDeepLinkedOrderNewlyCreated) {
        this.deepLinkedOrder = undefined;
      }
    });
  }

  orderJobsFetchEnd(orderId: string, jobs: Job[]) {
    runInAction(() => {
      this.isLoadingOrderJobs.set(orderId, false);
      this.orderJobs.set(orderId, jobs);

      if (this.deepLinkedOrder?.id === orderId) {
        // when fetching jobs for a deep-linked order, chances are that the API will return
        // no results, e.g.: when the DB indexes of the jobs table are being updated.
        // if so, we do not clear the deep-linked order right away; we need to keep it until
        // a job update is received via RTU. we do this to properly report the user that the
        // jobs under the order he just created are still pending to be displayed.
        if (jobs.length || !this.isDeepLinkedOrderNewlyCreated) {
          this.deepLinkedOrder = undefined;
        }
      }
    });
  }

  orderJobAcceptStart(jobId: string) {
    runInAction(() => {
      this.isAcceptingOrRejectingJobs.set(jobId, true);
    });
  }

  orderJobAcceptEnd(jobId: string, job?: Job) {
    runInAction(() => {
      this.isAcceptingOrRejectingJobs.set(jobId, false);

      if (job) {
        this.upsertJob(job);
      }
    });
  }

  orderJobCancelStart(jobId: string) {
    runInAction(() => {
      this.cancellingJobId = jobId;
    });
  }

  orderJobCancelEnd(job?: Job) {
    runInAction(() => {
      this.cancellingJobId = '';

      if (job) {
        this.upsertJob(job);
      }
    });
  }

  orderJobDeleteStart(jobId: string) {
    runInAction(() => {
      this.deletingJobId = jobId;
    });
  }

  orderJobDeleteEnd(jobId: string, deleteDidFail?: boolean) {
    runInAction(() => {
      this.deletingJobId = undefined;

      if (!deleteDidFail) {
        this.removeJob(jobId);
      }
    });
  }

  orderJobDuplicateStart(jobId: string) {
    runInAction(() => {
      this.duplicatingJobId = jobId;
    });
  }

  orderJobDuplicateEnd(job?: Job) {
    runInAction(() => {
      this.duplicatingJobId = '';

      if (job) {
        this.upsertJob(job);
      }
    });
  }

  orderJobUpdateStart(jobId: string) {
    runInAction(() => {
      this.isUpdatingOrderJob.set(jobId, true);
    });
  }

  orderJobUpdateEnd(jobId: string, updatedJob?: Job) {
    runInAction(() => {
      this.isUpdatingOrderJob.set(jobId, false);

      if (updatedJob) {
        this.upsertJob(updatedJob);
      }
    });
  }

  orderJobRejectStart(jobId: string) {
    runInAction(() => {
      this.isAcceptingOrRejectingJobs.set(jobId, true);
    });
  }

  orderJobRejectEnd(jobId: string, job?: Job) {
    runInAction(() => {
      this.isAcceptingOrRejectingJobs.set(jobId, false);

      if (job) {
        this.upsertJob(job);
      }
    });
  }

  orderJobSendStart(jobId: string) {
    runInAction(() => {
      this.isSendingJobs.set(jobId, true);
    });
  }

  orderJobSendEnd(jobId: string, job?: Job) {
    runInAction(() => {
      this.isSendingJobs.set(jobId, false);

      if (job) {
        this.upsertJob(job);
      }
    });
  }

  setHasUnfilteredResults(value: boolean) {
    runInAction(() => {
      this.hasUnfilteredResults = value;
    });
  }

  setIsCheckingOtherDateResults(value: boolean) {
    runInAction(() => {
      this.isCheckingOtherDateResults = value;
    });
  }

  setHasResultsOutsideDateRange(value: boolean) {
    runInAction(() => {
      this.hasResultsOutsideDateRange = value;
    });
  }

  setAutoExtendedFromRange(
    range: { term: string; startDate?: string; endDate?: string } | null,
  ) {
    runInAction(() => {
      this.autoExtendedFromRange = range;
    });
  }

  markDateScopeUserChosen() {
    runInAction(() => {
      this.userChosenDateScopeTerm = this.filters.search?.trim() ?? '';
    });
  }

  clearAllFiltersExceptSearch() {
    runInAction(() => {
      const search = this.filters.search;
      localStorage.removeItem(OrdersDispatchStorageKeys.APPLIED_FILTERS);
      this.resetOrders({ search });
    });
  }

  resetOrders(filters: Filters = {}, ordersSort?: OrderOption<OrdersSort>) {
    runInAction(() => {
      this.filters = filters;
      this.hasMore = false;
      this.hasResultsOutsideDateRange = false;
      this.hasUnfilteredResults = false;
      this.hasOrderJobsFetchFailed = new Map();
      this.isCheckingOtherDateResults = false;
      this.isLoadingOrderJobs = new Map();
      this.isLoadingOrderProject = new Map();
      this.isLoadingOrders = false;
      this.orders = this.deepLinkedOrder ? [this.deepLinkedOrder] : [];
      this.orderJobs = new Map();
      this.orderProject = new Map();
      this.ordersSort = { ...this.ordersSort, ...ordersSort };
      this.pagination = { ...defaultPaginationState };
    });
  }

  resetOrderJobs() {
    runInAction(() => {
      this.hasOrderJobsFetchFailed = new Map();
      this.isLoadingOrderJobs = new Map();
      this.orderJobs = new Map();
    });
  }

  retrieveStoredFilters() {
    const storedFilters = localStorage.getItem(OrdersDispatchStorageKeys.APPLIED_FILTERS);
    const parsedFilters = (storedFilters ? JSON.parse(storedFilters) : {}) as Filters;

    // default start date today if this is the very first the time the screen is loaded
    if (!storedFilters) {
      parsedFilters.startDate = dayjs.tz().format('YYYY-MM-DD');
      parsedFilters.endDate = dayjs.tz().format('YYYY-MM-DD');
    }

    if (parsedFilters.startDate === TODAY_DATE_STORE_KEY) {
      parsedFilters.startDate = dayjs.tz().format('YYYY-MM-DD');
    }

    if (parsedFilters.endDate === TODAY_DATE_STORE_KEY) {
      parsedFilters.endDate = dayjs.tz().format('YYYY-MM-DD');
    }

    return parsedFilters;
  }

  restoreFilters() {
    runInAction(() => {
      this.filters = { ...this.filters, ...this.retrieveStoredFilters() };
      this.orderCardsExpandedState = { ...this.retrieveOrderCardsExpandedState() };
      this.ordersSort = { ...this.retrieveStoredOrdersSort() };
    });
  }

  setDefaultOrdersSort(overrideOrdersSort?: OrderOption<OrdersSort>) {
    const nextDefaultOrdersSort = overrideOrdersSort ?? defaultOrdersSort;

    // OrdersView calls this from its render body, so re-renders repeat it with an
    // unchanged default; only a default this instance has not evaluated reaches storage.
    if (
      this.lastEvaluatedDefaultOrdersSort &&
      areOrderSortOptionsEqual(this.lastEvaluatedDefaultOrdersSort, nextDefaultOrdersSort)
    ) {
      return;
    }

    this.lastEvaluatedDefaultOrdersSort = { ...nextDefaultOrdersSort };

    // A stored pick outranks this default, so each browser keeps the default it last applied:
    // once that differs the company setting moved, and the stored pick gives way to it. A
    // browser with no marker yet reads as changed, so the first load after this shipped drops
    // any pick made before it — without that, every dispatcher who ever used the Order By
    // dropdown would stay immune to the setting forever.
    const lastAppliedDefaultOrdersSort = this.retrieveLastAppliedDefaultOrdersSort();
    const defaultOrdersSortHasChanged =
      !lastAppliedDefaultOrdersSort ||
      !areOrderSortOptionsEqual(lastAppliedDefaultOrdersSort, nextDefaultOrdersSort);

    if (defaultOrdersSortHasChanged) {
      localStorage.removeItem(OrdersDispatchStorageKeys.APPLIED_ORDERS_SORT);
      this.persistLastAppliedDefaultOrdersSort(nextDefaultOrdersSort);
    }

    const storedOrdersSort = defaultOrdersSortHasChanged
      ? null
      : localStorage.getItem(OrdersDispatchStorageKeys.APPLIED_ORDERS_SORT);

    runInAction(() => {
      if (
        !areOrderSortOptionsEqual(this.defaultOrdersSortOption, nextDefaultOrdersSort)
      ) {
        this.defaultOrdersSortOption = { ...nextDefaultOrdersSort };
      }

      if (
        !storedOrdersSort &&
        !areOrderSortOptionsEqual(this.ordersSort, nextDefaultOrdersSort)
      ) {
        this.ordersSort = { ...nextDefaultOrdersSort };
      }
    });
  }

  persistFilters(filters: Filters, isDateScopeAutoExtended: boolean) {
    runInAction(() => {
      const toPersist: Array<keyof Filters> = [
        'aiReviewStatuses',
        'collaboratorIds',
        'customerAccountIds',
        'departmentIds',
        'dispatchNumberIds',
        'dispatchStatuses',
        'driverIds',
        'dropoffSites',
        'endDate',
        'equipmentIds',
        'equipmentOwnerCompanyIds',
        'equipmentTypes',
        'foremen',
        'hasUnreadMessages',
        'jobStates',
        'materialIds',
        'pickUpSites',
        'projectIds',
        'projectsExternalIds',
        'refinedOrderStates',
        'requesterIds',
        'salespersonIds',
        'serviceClassIds',
        'startDate',
        'vendorAccountIds',
      ];

      const shouldPersistFilters = Object.keys(filters).some((key) => {
        return toPersist.includes(key as keyof Filters);
      });

      if (!shouldPersistFilters) {
        return;
      }

      const values = pick(filters, toPersist);

      // An auto-extended scope belongs to the system, not the dispatcher, so their prior range
      // stays in storage instead of the cleared dates the extend put in memory.
      if (isDateScopeAutoExtended) {
        values.startDate = this.autoExtendedFromRange?.startDate;
        values.endDate = this.autoExtendedFromRange?.endDate;
      }

      const dates = pick(values, ['startDate', 'endDate']);

      if (
        dates.startDate &&
        dates.startDate === dates.endDate &&
        dayjs.tz(dates.startDate).isToday()
      ) {
        values.startDate = TODAY_DATE_STORE_KEY;
        values.endDate = TODAY_DATE_STORE_KEY;
      }

      localStorage.setItem(
        OrdersDispatchStorageKeys.APPLIED_FILTERS,
        JSON.stringify(values),
      );
    });
  }

  persistOrderCardsExpandedState() {
    localStorage.setItem(
      this.orderCardsExpandedStateStorageKey,
      this.orderCardsExpandedState.allCards,
    );
  }

  retrieveOrderCardsExpandedState() {
    const storedExpandedState = localStorage.getItem(
      this.orderCardsExpandedStateStorageKey,
    );
    const parsedExpandedState: OrderCardsExpandedState = {
      allCards: storedExpandedState
        ? storedExpandedState
        : this.orderCardsDefaultExpandedState,
      cardsByOrder: new Map(),
    };

    return parsedExpandedState;
  }

  setFilters(filters: Filters, merge?: boolean, doNotPersist?: boolean) {
    // Read before anything below mutates the term or the dates: the getter compares the recorded
    // term against the CURRENT one, so a search edit or clear would flip it false and let the
    // temporary all-dates scope overwrite the dispatcher's saved range.
    const wasDateScopeAutoExtended = this.isDateScopeAutoExtended;
    const newFilters = merge ? { ...this.filters, ...filters } : filters;

    // NOTE: setFilters no longer touches autoExtendedFromRange. Banner visibility is DERIVED in
    // OrdersView from the stored term matching the current search + all-dates being selected
    // (TRE-15949), so the banner self-hides when the term changes or the dates are re-applied —
    // no fragile teardown heuristic here.

    // Sync refinedOrderStates when jobStates changes
    if ('jobStates' in filters) {
      const jobStates = newFilters.jobStates ?? [];
      const isCanceledJobSelected = jobStates.includes(JobState.CANCELED);
      const currentRefinedOrderStates = newFilters.refinedOrderStates ?? [];
      const hasCanceledOrderState = currentRefinedOrderStates.includes(
        OrderState.CANCELED,
      );

      if (isCanceledJobSelected && !hasCanceledOrderState) {
        newFilters.refinedOrderStates = [
          ...currentRefinedOrderStates,
          OrderState.CANCELED,
        ];
      } else if (!isCanceledJobSelected && hasCanceledOrderState) {
        const withoutCanceled = currentRefinedOrderStates.filter(
          (state) => state !== OrderState.CANCELED,
        );
        newFilters.refinedOrderStates =
          withoutCanceled.length > 0 ? withoutCanceled : undefined;
      }
    }

    // Dispatch status counts are per day, so a status filter only lines up with them on one day.
    const isSingleDay = Boolean(
      newFilters.startDate && newFilters.startDate === newFilters.endDate,
    );

    if (!isSingleDay) {
      newFilters.dispatchStatuses = undefined;
    }

    const committedTermChanged =
      'search' in filters &&
      (filters.search?.trim() ?? '') !== (this.filters.search?.trim() ?? '');

    if (committedTermChanged) {
      runInAction(() => {
        this.userChosenDateScopeTerm = null;
      });
    }

    runInAction(() => {
      this.deepLinkedOrder = undefined;
      this.deepLinkedJobId = undefined;
    });

    const previousFilters = this.retrieveStoredFilters();
    const previousTab = (localStorage.getItem(OrdersDispatchStorageKeys.TAB_PARAM) ||
      DEFAULT_TAB) as StatusTabValue;
    const orderStates =
      'orderStates' in filters
        ? filters.orderStates
        : (this.filters.orderStates ?? orderStatesByStatusTab[previousTab]);

    this.resetOrders({ ...previousFilters, ...newFilters, orderStates });

    if (!doNotPersist) {
      this.persistFilters(newFilters, wasDateScopeAutoExtended);
    }
  }

  retrieveStoredOrdersSort() {
    const storedOrdersSort = localStorage.getItem(
      OrdersDispatchStorageKeys.APPLIED_ORDERS_SORT,
    );
    const parsedOrdersSort = (
      storedOrdersSort
        ? JSON.parse(storedOrdersSort)
        : { ...this.defaultOrdersSortOption }
    ) as OrderOption<OrdersSort>;

    return parsedOrdersSort;
  }

  persistOrdersSort(option: OrderOption<OrdersSort>) {
    localStorage.setItem(
      OrdersDispatchStorageKeys.APPLIED_ORDERS_SORT,
      JSON.stringify(option),
    );
  }

  retrieveLastAppliedDefaultOrdersSort() {
    const storedValue = localStorage.getItem(
      OrdersDispatchStorageKeys.APPLIED_ORDERS_SORT_DEFAULT,
    );

    if (storedValue === null) {
      return null;
    }

    // localStorage is untyped, user-editable input, and this runs in a render body where a
    // throw blanks the page: anything but the exact OrderOption shape reads as unset.
    let parsedValue: unknown;
    try {
      parsedValue = JSON.parse(storedValue);
    } catch {
      return null;
    }

    if (parsedValue === null || typeof parsedValue !== 'object') {
      return null;
    }

    const { orderBy, sortBy } = parsedValue as Partial<OrderOption<OrdersSort>>;

    if (typeof orderBy !== 'string' || (sortBy !== 'asc' && sortBy !== 'desc')) {
      return null;
    }

    return { orderBy, sortBy };
  }

  persistLastAppliedDefaultOrdersSort(option: OrderOption<OrdersSort>) {
    localStorage.setItem(
      OrdersDispatchStorageKeys.APPLIED_ORDERS_SORT_DEFAULT,
      JSON.stringify(option),
    );
  }

  setOrdersSort(option: OrderOption<OrdersSort>) {
    runInAction(() => {
      this.resetOrders(this.filters, option);
      this.persistOrdersSort(option);
    });
  }

  upsertOrder(order: Order) {
    if (this.preventUpsertOrder) {
      return;
    }

    runInAction(() => {
      this.updateOrdersWaitingForTrucksCountToMatchCountInSummary(order);

      const index = this.orders.findIndex(({ id }) => id === order.id);

      if (index > -1) {
        this.orders.splice(index, 1, order);
      }
    });
  }

  upsertJob(job: Job) {
    if (!job.order?.id) {
      return;
    }

    const orderId = job.order.id;
    const orderJobs = this.orderJobs.get(orderId);

    this.clearJobsWaitingForRTU([job.jobId]);

    if (orderJobs) {
      const index = orderJobs.findIndex(({ id }) => id === job.id);

      runInAction(() => {
        if (index > -1) {
          orderJobs.splice(index, 1, job);
        } else {
          orderJobs.push(job);
        }

        this.orderJobs.set(orderId, orderJobs);
      });
    }
  }

  removeJob(jobId: string) {
    const orderEntry = [...this.orderJobs].find(([, jobs]) =>
      jobs.some(({ id }) => id === jobId),
    );

    if (orderEntry) {
      const [orderId, jobs] = orderEntry;

      const index = jobs.findIndex(({ id }) => id === jobId);

      if (index > -1) {
        runInAction(() => {
          // We don't want to display the order if it doesn't have any jobs
          this.preventUpsertOrder = true;
          const unassignedJobId = jobs[index].jobId;
          jobs.splice(index, 1);

          if (jobs.length === 0) {
            // Remove the entire order if there are no more jobs
            const unassignedOrder = this.orders.find(({ id }) => id === orderId);
            const unassignedOrderId = unassignedOrder?.orderId;
            this.orders = this.orders.filter(({ id }) => id !== orderId);
            rootStore.toasterStore.push(
              alert(
                t('dispatch.order.order_id_unassigned', { id: unassignedOrderId }),
                AlertTypes.success,
              ),
            );
          } else {
            // Remove the individual job from the order
            this.orderJobs.set(orderId, jobs);
            rootStore.toasterStore.push(
              alert(
                t('dispatch.job.job_id_removed', { id: unassignedJobId }),
                AlertTypes.success,
              ),
            );
          }

          this.preventUpsertOrder = false;
        });
      }
    }
  }

  toggleSidebar(isCollapsed?: boolean) {
    runInAction(() => {
      this.isSidebarCollapsed = isNil(isCollapsed)
        ? !this.isSidebarCollapsed
        : isCollapsed;
    });
  }

  clearDriverPanelAssignees() {
    runInAction(() => {
      this.driverPanelAssignees = [];
    });
  }

  setDriverPanelAssignees(data: Array<DriverTypeahead | AccountCapacityTypeahead>) {
    runInAction(() => {
      this.driverPanelAssignees = uniqBy(
        [...data, ...this.driverPanelAssignees],
        (assignee) => assignee.id,
      );
    });
  }

  get draggedAssigneeTruckCapacity() {
    if (!(this.draggedAssignee instanceof AccountCapacityTypeahead)) {
      return undefined;
    }

    return this.draggedAssignee.equipmentTypeStats.find(
      (stat) => stat.equipmentType.id === this.draggedAssigneeEquipmentTypeId,
    );
  }

  setDraggedAssignee(
    assignee?: DriverTypeahead | AccountCapacityTypeahead,
    equipmentTypeId?: string,
  ) {
    runInAction(() => {
      this.draggedAssignee = assignee;
      this.draggedAssigneeEquipmentTypeId = equipmentTypeId;
    });
  }

  switchOrderCardsExpandedState(
    storageKey: OrderCardsExpandedStateStorageKey,
    defaultExpandedState: ExpandedState,
  ) {
    runInAction(() => {
      this.orderCardsDefaultExpandedState = defaultExpandedState;

      if (storageKey === this.orderCardsExpandedStateStorageKey) {
        return;
      }

      this.orderCardsExpandedStateStorageKey = storageKey;
      this.orderCardsExpandedState = this.retrieveOrderCardsExpandedState();
    });
  }

  isOrderCardExpanded(orderId: string) {
    return isOrderExpanded(this.orderCardsExpandedState, orderId);
  }

  setOrderCardsExpandedState(state: OrderCardsExpandedState) {
    runInAction(() => {
      this.orderCardsExpandedState = state;
      this.persistOrderCardsExpandedState();
    });
  }

  toggleOrderCardExpandedState(orderId: string) {
    runInAction(() => {
      const { allCards, cardsByOrder } = this.orderCardsExpandedState;
      const currentExpandedState = cardsByOrder.get(orderId) ?? allCards;
      let newExpandedState: string = ExpandedState.CLOSED;

      if (currentExpandedState === ExpandedState.CLOSED) {
        newExpandedState = ExpandedState.OPEN;
      }

      const newCardsByOrder = new Map(cardsByOrder);
      newCardsByOrder.set(orderId, newExpandedState);

      this.orderCardsExpandedState = { allCards, cardsByOrder: newCardsByOrder };
      this.persistOrderCardsExpandedState();
    });
  }

  setDeepLinkedOrder(): void;
  setDeepLinkedOrder(order: Order, options: DeepLinkOptions): void;
  setDeepLinkedOrder(order?: Order, options?: DeepLinkOptions) {
    const job = options?.job;

    runInAction(() => {
      this.deepLinkedOrder = order;
      this.deepLinkedJobId = order ? job?.id : undefined;
      this.isDeepLinkedOrderNewlyCreated =
        Boolean(order) && options?.source === 'created';

      if (order) {
        // The jobs list reads these dates in the dispatch timezone, not the browser's.
        const boardDate = (job?.jobStartAt ?? order.jobStartAt)
          ?.tz()
          .format('YYYY-MM-DD');

        this.resetOrders({
          search: order.orderId,
          orderStates: ORDERS_TAB_ORDER_STATES,
          startDate: boardDate,
          endDate: boardDate,
        });
        this.markDateScopeUserChosen();

        const cardsByOrder = new Map(this.orderCardsExpandedState.cardsByOrder);
        cardsByOrder.set(order.id, ExpandedState.OPEN);
        this.setOrderCardsExpandedState({
          ...this.orderCardsExpandedState,
          cardsByOrder,
        });

        this.ordersFetchEnd([order]);
      }
    });
  }

  /** Dispatch resets its filters as it mounts, so a deep link from another page waits for it. */
  setPendingDeepLink(pendingDeepLink?: PendingDeepLink) {
    runInAction(() => {
      this.pendingDeepLink = pendingDeepLink;
    });
  }

  applyPendingDeepLink() {
    const { pendingDeepLink } = this;

    if (!pendingDeepLink) {
      return;
    }

    this.setPendingDeepLink();
    this.setDeepLinkedOrder(pendingDeepLink.order, {
      job: pendingDeepLink.job,
      source: 'existing',
    });
  }

  clearDeepLinkedJob() {
    runInAction(() => {
      this.deepLinkedJobId = undefined;
    });
  }

  /** Leaving Dispatch drops every link, so a later visit never reopens an old order. */
  clearDeepLink() {
    runInAction(() => {
      this.pendingDeepLink = undefined;
      this.deepLinkedOrder = undefined;
      this.deepLinkedJobId = undefined;
      this.isDeepLinkedOrderNewlyCreated = false;
    });
  }

  findJobAssociatedOrder(jobId: string) {
    const [orderId] =
      [...this.orderJobs].find(([, jobs]) => {
        return jobs.find((job) => job.id === jobId);
      }) ?? [];

    if (orderId) {
      return this.orders.find((order) => order.id === orderId) ?? null;
    }

    return null;
  }

  setOrdersCount(counters: OrdersCount) {
    runInAction(() => {
      this.ordersCount = { ...this.ordersCount, ...counters };
    });
  }

  setPendingFiltersUpdateAfterAssigneeUpdate(pendingUpdate: {
    label: string;
    value: string;
  }) {
    runInAction(() => {
      this.pendingFiltersUpdateAfterAssigneeUpdate = pendingUpdate;
      this.pendingFiltersUpdateAfterAssigneeUpdateHasBeenCommitedInLocalStorageAvailebleOptions = false;
    });
  }

  clearPendingFiltersUpdateAfterAssigneeUpdate() {
    runInAction(() => {
      this.pendingFiltersUpdateAfterAssigneeUpdate = undefined;
    });
  }

  commitPendingFiltersUpdateAfterAssigneeUpdateInLocalStorageAvailebleOptions() {
    runInAction(() => {
      this.pendingFiltersUpdateAfterAssigneeUpdateHasBeenCommitedInLocalStorageAvailebleOptions = true;
    });
  }

  setJobsWaitingForRTU(jobIds: string[]) {
    runInAction(() => {
      const jobsPendingRTUResponse: OrdersDispatchStore['jobsWaitingForRTU'] = [];

      for (const orderEntry of this.orderJobs) {
        const [, jobs] = orderEntry;

        for (const job of jobs) {
          if (jobIds.includes(job.id)) {
            this.orderJobUpdateStart(job.id);
            jobsPendingRTUResponse.push({ jobId: job.id, friendlyJobId: job.jobId });
          }
        }
      }

      setTimeout(() => {
        this.clearJobsWaitingForRTU(
          jobsPendingRTUResponse.map((job) => job.friendlyJobId),
        );
      }, RTU_WAITING_TIMEOUT_IN_MS);

      this.jobsWaitingForRTU = uniqBy(
        [...this.jobsWaitingForRTU, ...jobsPendingRTUResponse],
        (job) => job.jobId,
      );
    });
  }

  clearJobsWaitingForRTU(friendlyJobIds: string[]) {
    runInAction(() => {
      const updatedList = this.jobsWaitingForRTU.filter((entry) => {
        const hasPendingUpdate = friendlyJobIds.includes(entry.friendlyJobId);

        if (hasPendingUpdate) {
          this.orderJobUpdateEnd(entry.jobId);
        }

        return !hasPendingUpdate;
      });

      this.jobsWaitingForRTU = updatedList;
    });
  }
}

export default OrdersDispatchStore;
